import {useState, useEffect} from "react"
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, IconButton, Chip, TablePagination, MenuItem, Select,
    FormControl, InputLabel, InputAdornment, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, Grid, Divider, List, ListItem, ListItemText, Alert,
} from "@mui/material"
import {
    Search as SearchIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon,
    LocalShipping as LocalShippingIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../../config/axios-config"

const ORDER_STATUSES = ["ALL", "PENDING", "CONFIRMED", "PROCESSING", "SHIPPED", "DELIVERED", "CANCELLED"]

export default function TabAdminOrders({tabValue}) {
    const {t} = useTranslation()
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [totalElements, setTotalElements] = useState(0)
    const [searchQuery, setSearchQuery] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const [statusFilter, setStatusFilter] = useState("ALL")
    const [detailDialog, setDetailDialog] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [orderItems, setOrderItems] = useState([])

    const fetchOrders = async () => {
        setLoading(true)
        setErrorMessage("")
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                size: rowsPerPage.toString(),
                ...(appliedSearch && {search: appliedSearch}),
                ...(statusFilter !== "ALL" && {status: statusFilter}),
            })
            const response = await axiosInstance.get(`/api/v1/admin/orders?${params}`)
            setOrders(response.data.content || [])
            setTotalElements(response.data.totalElements || 0)
        } catch (error) {
            console.error("Error fetching orders:", error)
            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
        }
        setLoading(false)
    }

    useEffect(() => {
        if (tabValue === 2) fetchOrders()
    }, [tabValue, page, rowsPerPage, statusFilter, appliedSearch])

    const handleSearch = () => {
        setPage(0)
        setAppliedSearch(searchQuery.trim())
    }

    const handleViewOrder = async (order) => {
        setSelectedOrder(order)
        try {
            const response = await axiosInstance.get(`/api/v1/admin/orders/${order.id}/items`)
            setOrderItems(response.data || [])
            setDetailDialog(true)
        } catch (error) {
            console.error("Error fetching order items:", error)
            setErrorMessage(error.response?.data?.message || t("AdminRequestFailed"))
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case "PENDING": return "warning"
            case "CONFIRMED": return "info"
            case "PROCESSING": return "primary"
            case "SHIPPED": return "secondary"
            case "DELIVERED": return "success"
            case "CANCELLED": return "error"
            default: return "default"
        }
    }

    if (tabValue !== 2) return null

    return (
        <Box>
            {errorMessage && <Alert severity="error" sx={{mb: 2}}>{errorMessage}</Alert>}
            {/* Filters */}
            <Box sx={{display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center"}}>
                <TextField
                    size="small"
                    placeholder={t("SearchOrders") || "Search by order number..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    }}
                    sx={{minWidth: 250}}
                />
                <FormControl size="small" sx={{minWidth: 160}}>
                    <InputLabel>{t("Status") || "Status"}</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label={t("Status") || "Status"}>
                        {ORDER_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                                {status === "ALL" ? t("All") || "All" : t(status) || status}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{flex: 1}} />
                <Tooltip title={t("Refresh") || "Refresh"}>
                    <IconButton onClick={fetchOrders}><RefreshIcon /></IconButton>
                </Tooltip>
            </Box>

            {/* Orders Table */}
            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead>
                        <TableRow sx={{bgcolor: "grey.50"}}>
                            <TableCell>{t("OrderNumber") || "Order #"}</TableCell>
                            <TableCell>{t("Pharmacy") || "Pharmacy"}</TableCell>
                            <TableCell>{t("Distributor") || "Distributor"}</TableCell>
                            <TableCell>{t("Status") || "Status"}</TableCell>
                            <TableCell align="right">{t("TotalAmount") || "Total Amount"}</TableCell>
                            <TableCell>{t("Date") || "Date"}</TableCell>
                            <TableCell align="center">{t("Actions") || "Actions"}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{py: 4}}>
                                    <Typography color="text.secondary">{t("Loading") || "Loading..."}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{py: 4}}>
                                    <Typography color="text.secondary">{t("NoOrdersFound") || "No orders found"}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order) => (
                                <TableRow key={order.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">
                                            #{order.orderNumber}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{order.pharmacyName || "-"}</TableCell>
                                    <TableCell>{order.distributorName || "-"}</TableCell>
                                    <TableCell>
                                        <Chip label={t(order.status) || order.status} color={getStatusColor(order.status)} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600}>
                                            {order.totalAmount?.toLocaleString() || 0}֏
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={t("ViewDetails") || "View Details"}>
                                            <IconButton size="small" onClick={() => handleViewOrder(order)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    labelRowsPerPage={t("RowsPerPage") || "Rows per page"}
                />
            </TableContainer>

            {/* Order Detail Dialog */}
            <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                        <LocalShippingIcon color="primary" />
                        <Typography variant="h6">
                            {t("Order") || "Order"} #{selectedOrder?.orderNumber}
                        </Typography>
                        {selectedOrder && (
                            <Chip label={t(selectedOrder.status) || selectedOrder.status} color={getStatusColor(selectedOrder.status)} size="small" />
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Box>
                            <Grid container spacing={2} sx={{mb: 3}}>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="text.secondary">{t("Pharmacy") || "Pharmacy"}</Typography>
                                    <Typography fontWeight={500}>{selectedOrder.pharmacyName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="text.secondary">{t("Distributor") || "Distributor"}</Typography>
                                    <Typography fontWeight={500}>{selectedOrder.distributorName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="text.secondary">{t("Date") || "Date"}</Typography>
                                    <Typography fontWeight={500}>
                                        {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleString() : "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{mb: 2}}>{t("OrderItems") || "Order Items"}</Divider>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("Product") || "Product"}</TableCell>
                                            <TableCell align="center">{t("Quantity") || "Qty"}</TableCell>
                                            <TableCell align="right">{t("Price") || "Price"}</TableCell>
                                            <TableCell align="right">{t("Total") || "Total"}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell align="center">{item.quantity}</TableCell>
                                                <TableCell align="right">{item.price?.toLocaleString()}֏</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 600}}>
                                                    {(item.quantity * item.price)?.toLocaleString()}֏
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={3} align="right" sx={{fontWeight: 700, borderBottom: "none"}}>
                                                {t("TotalAmount") || "Total Amount"}:
                                            </TableCell>
                                            <TableCell align="right" sx={{fontWeight: 700, color: "primary.main", fontSize: "1.1rem", borderBottom: "none"}}>
                                                {selectedOrder.totalAmount?.toLocaleString()}֏
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog(false)}>{t("Close") || "Close"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
